import { useRef, useState } from "react";
import { uploadLogo, deleteLogo } from "../../lib/logoUpload";
import { colors, fontSize, fontWeight, radius, space } from "../../lib/tokens";

interface Props {
  label: string;
  value: string | null;
  tenantId: string;
  variant: "light" | "dark";
  onChange: (next: string | null) => void;
  error?: string | null;
}

const ACCEPT = "image/png,image/jpeg,image/webp,image/svg+xml";
const PREVIEW_BG = { light: "#FAFAF7", dark: "#0E1116" } as const;

export function AssetUploadField({ label, value, tenantId, variant, onChange, error }: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState<"upload" | "delete" | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  const onPick = () => inputRef.current?.click();

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so re-picking same file fires onChange
    if (!file) return;
    setBusy("upload");
    setLocalError(null);
    try {
      const url = await uploadLogo(tenantId, variant, file);
      onChange(url);
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  const onClear = async () => {
    setBusy("delete");
    setLocalError(null);
    try {
      await deleteLogo(tenantId, variant);
      onChange(null);
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  const showError = error ?? localError;

  return (
    <div style={{ marginBottom: space.lg }}>
      <span style={{ display: "block", fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.stone, marginBottom: 6 }}>
        {label}
      </span>
      <div style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: 12, background: PREVIEW_BG[variant],
        border: `1px solid ${colors.fog}`, borderRadius: radius.md,
      }}>
        {value ? (
          <img src={value} alt="" style={{ height: 48, width: "auto", maxWidth: 240, objectFit: "contain" }} />
        ) : (
          <span style={{ fontSize: fontSize.sm, color: variant === "dark" ? colors.stoneSoft : colors.stone }}>
            Sem logo
          </span>
        )}
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button type="button" onClick={onPick} disabled={busy !== null}
                  style={{ height: 32, padding: "0 12px", background: colors.white, color: colors.ink,
                           border: `1px solid ${colors.fog}`, borderRadius: radius.sm,
                           fontSize: fontSize.sm, fontWeight: fontWeight.medium, cursor: "pointer" }}>
            {busy === "upload" ? "Enviando..." : value ? "Trocar" : "Enviar logo"}
          </button>
          {value && (
            <button type="button" onClick={onClear} disabled={busy !== null}
                    style={{ height: 32, padding: "0 12px", background: colors.white, color: colors.error,
                             border: `1px solid ${colors.fog}`, borderRadius: radius.sm,
                             fontSize: fontSize.sm, fontWeight: fontWeight.medium, cursor: "pointer" }}>
              {busy === "delete" ? "Removendo..." : "Remover"}
            </button>
          )}
        </div>
        <input ref={inputRef} type="file" accept={ACCEPT} onChange={onFile} style={{ display: "none" }} />
      </div>
      {showError && (
        <span style={{ display: "block", fontSize: fontSize.sm, color: colors.error, marginTop: 4 }}>{showError}</span>
      )}
    </div>
  );
}
